import { Button } from "@/components/flexkit/Button";
import { Banner } from "@/components/flexkit/Banner";
import { Pill } from "@/components/podkit/status/Pill";
import { toast } from "@/components/podkit/toasts/use-toast";
import { ExternalLink } from "@/components/podkit/typography/ExternalLink";
import { Heading1 } from "@/components/podkit/typography/Headings";
import { Text as Paragraph } from "@/components/podkit/typography/Text";
import { FeatureItem } from "@/components/education/FeatureItem";
import { EducationPageContainer } from "@/components/education/EducationPageContainer";
import { ENTERPRISE_TRIAL_REQUESTED } from "@/components/tiering/TierViolationDialog";
import { usePreferenceValue } from "@/hooks/use-preference-value";
import { useSegmentTrack } from "@/hooks/use-segment";
import { useGetAccount } from "@/queries/account-queries";
import { KeyIcon, ShieldCheckIcon, UsersIcon } from "lucide-react";
import { type FC, useCallback } from "react";
import { useNavigate } from "react-router-dom";

export const OrganizationSecretsEducationPage: FC = () => {
    const segmentTrack = useSegmentTrack();
    const { data: account } = useGetAccount();
    const navigate = useNavigate();

    const { value: trialRequestedValue, setValueAsync: setTrialRequestedValue } =
        usePreferenceValue(ENTERPRISE_TRIAL_REQUESTED);
    const trialRequested = trialRequestedValue === "true";

    const handleRequestTrial = useCallback(async () => {
        try {
            segmentTrack("trial_requested", { type: "enterprise", email: account?.email });
            await setTrialRequestedValue("true");
            toast({
                title: "Trial requested!",
                description: "Our team is working on your request and will be in touch soon.",
            });
        } catch (error) {
            toast({
                title: "Failed to request trial",
                description: error instanceof Error ? error.message : "An unknown error occurred.",
            });
        }
    }, [account?.email, segmentTrack, setTrialRequestedValue]);

    const handleGoToPersonalSecrets = useCallback(() => {
        navigate("/settings/secrets");
    }, [navigate]);

    return (
        <EducationPageContainer>
            <div className="flex flex-col gap-2">
                <Pill variant="brand" size="lg" className="w-fit">
                    Available on Enterprise ✨
                </Pill>
                <Heading1>Secure organization-wide secrets management</Heading1>
                <Paragraph className="text-content-secondary text-md">
                    Organization-wide secrets provide a centralized way to manage sensitive information like API keys, tokens, and credentials that are automatically available across all environments in your organization.
                </Paragraph>
                
                <Banner
                    variant="info"
                    text="In the meantime, you can manage personal secrets that are available in your own environments."
                    action={{
                        text: "View personal secrets",
                        onClick: handleGoToPersonalSecrets,
                        "data-tracking-id": "go-to-personal-secrets-organization-secrets-education-page",
                        responsive: true,
                    }}
                />
                
                <div className="mt-2 flex flex-col gap-3 sm:flex-row">
                    <Button
                        size="md"
                        onClick={handleRequestTrial}
                        disabled={trialRequested}
                        data-tracking-id="request-trial-organization-secrets-education-page"
                    >
                        {trialRequested ? "Trial Requested" : "Request trial"}
                    </Button>
                    <Button variant="outline" size="md" asChild>
                        <ExternalLink
                            href="https://ona.com/docs/ona/organizations/organization-secrets"
                            iconSize="sm"
                        >
                            Learn more
                        </ExternalLink>
                    </Button>
                </div>
            </div>

            <div className="flex flex-col gap-3">
                <Paragraph className="text-base font-medium text-content-primary">Key benefits:</Paragraph>
                
                <FeatureItem
                    icon={<ShieldCheckIcon size={16} />}
                    variant="success"
                    title="Centralized security management"
                    description="Manage all your organization's secrets in one secure location. No more scattered API keys or credentials across different projects."
                />

                <FeatureItem
                    icon={<KeyIcon size={16} />}
                    variant="brand"
                    title="Automatic availability across environments"
                    description="Organization secrets are automatically injected into all environments, ensuring consistent access to required credentials without manual setup."
                />

                <FeatureItem
                    icon={<UsersIcon size={16} />}
                    variant="brand-purple"
                    title="Team collaboration and access control"
                    description="Enable secure collaboration by providing controlled access to shared secrets while maintaining security and audit trails."
                />
            </div>
        </EducationPageContainer>
    );
};
